<template>
    <section>
        <titlebar :title-stack="['Produção']" />
        <div class="section prd-page">
            <div class="prd-toolbar">
                <b-field class="prd-search">
                    <b-input
                        v-model.trim="filtro.procurar"
                        icon="magnify"
                        placeholder="Número do orçamento ou cliente"
                        @keyup.native.enter="buscar(1)"
                    />
                </b-field>
                <b-field label="Status" label-position="on-border" class="prd-status-filter">
                    <b-select v-model="filtro.situacao" expanded @input="buscar(1)">
                        <option :value="0">Todos</option>
                        <option :value="1">Em fabricação</option>
                        <option :value="2">Entregue</option>
                    </b-select>
                </b-field>
                <b-button type="is-light" icon-left="filter-variant" @click="filtrosAbertos = !filtrosAbertos">Filtros</b-button>
                <b-button type="is-primary" icon-left="magnify" @click="buscar(1)">Buscar</b-button>
            </div>

            <transition name="fade">
                <div v-if="filtrosAbertos" class="prd-filter-panel columns is-multiline">
                    <div class="column is-3">
                        <b-field label="Data inicial">
                            <b-datepicker v-model="filtro.dataInicial" icon="calendar" trap-focus />
                        </b-field>
                    </div>
                    <div class="column is-3">
                        <b-field label="Data final">
                            <b-datepicker v-model="filtro.dataFinal" icon="calendar" trap-focus />
                        </b-field>
                    </div>
                    <div class="column is-6 is-flex is-align-items-flex-end">
                        <b-button type="is-text" icon-left="broom" @click="limpar">Limpar filtros</b-button>
                    </div>
                </div>
            </transition>

            <div class="prd-table-card">
                <b-table
                    :data="resultado.itens || []"
                    :loading="loading"
                    paginated
                    backend-pagination
                    :total="resultado.total"
                    :per-page="filtro.porPagina"
                    :current-page.sync="filtro.pagina"
                    @page-change="buscar"
                    mobile-cards
                    hoverable
                    narrowed
                >
                    <b-table-column field="numero" label="Orçamento" width="130" v-slot="props">
                        <strong>{{ props.row.numero }}</strong>
                        <small class="prd-origin">v{{ props.row.versao }}</small>
                    </b-table-column>
                    <b-table-column field="dataHoraCadastro" label="Criado" width="185" v-slot="props">
                        {{ dataHora(props.row.dataHoraCadastro) }}
                        <small class="prd-subtext"><b>Responsável:</b> {{ props.row.responsavelNome || 'Não informado' }}</small>
                    </b-table-column>
                    <b-table-column field="clienteNome" label="Cliente" v-slot="props">
                        <strong>{{ props.row.clienteNome || 'Não informado' }}</strong>
                    </b-table-column>
                    <b-table-column label="Progresso" width="200" v-slot="props">
                        <span>
                            <strong>{{ props.row.itensConcluidos }}/{{ props.row.itensProducao }}</strong> itens
                            <small class="prd-subtext">Valor: <strong v-if="podeVerComercial" class="prd-value">{{ moeda(props.row.valorTotalComercial) }}</strong><span v-else>—</span></small>
                        </span>
                    </b-table-column>
                    <b-table-column field="dataHoraEntrega" label="Status" width="150" v-slot="props">
                        <b-tag rounded :type="props.row.dataHoraEntrega ? 'is-success is-light' : 'is-info is-light'">
                            {{ props.row.dataHoraEntrega ? 'Entregue' : 'Em fabricação' }}
                        </b-tag>
                    </b-table-column>
                    <b-table-column field="dataPagamento" label="Data de Pagamento" width="125" v-slot="props">
                        {{ data(props.row.dataPagamento) || 'Não informado' }}
                    </b-table-column>
                    <b-table-column field="dataEntrega" label="Prazo de Entrega" width="125" v-slot="props">
                        {{ data(props.row.dataEntrega) || 'Não informado' }}
                    </b-table-column>
                    <b-table-column label="" width="100" v-slot="props">
                        <div class="prd-row-actions">
                            <b-button
                                v-if="podeCancelar(props.row)"
                                type="is-danger"
                                size="is-small"
                                icon-left="close-circle-outline"
                                title="Cancelar produção"
                                @click="confirmarCancelamento(props.row)"
                            />
                            <b-button
                                type="is-light"
                                size="is-small"
                                icon-left="chevron-right"
                                tag="router-link"
                                :to="{ name: 'prd-producao-detalhe', params: { id: props.row.id } }"
                                title="Abrir produção"
                            />
                        </div>
                    </b-table-column>
                    <template #empty>
                        <prd-empty-state
                            icon="package-variant-closed"
                            title="Nenhum orçamento na produção"
                            description="A produção exibe orçamentos aprovados para fabricação."
                        />
                    </template>
                </b-table>
            </div>
        </div>
    </section>
</template>

<script>
import http from '@/http'
import { mapGetters, mapState } from 'vuex'
import titlebar from '@/components/titlebar.vue'
import prdEmptyState from '@/components/prd/prdEmptyState.vue'

export default {
    name: 'PrdProducao',
    components: { titlebar, prdEmptyState },
    data() {
        const situacaoDaRota = Number(this.$route.query.situacao)
        return {
            loading: false,
            filtrosAbertos: false,
            resultado: { itens: [], total: 0 },
            filtro: {
                procurar: '',
                situacao: [1, 2].includes(situacaoDaRota) ? situacaoDaRota : 0,
                dataInicial: null,
                dataFinal: null,
                pagina: 1,
                porPagina: 20,
            },
        }
    },
    computed: {
        ...mapState(['unidadeId']),
        ...mapGetters(['isInRole']),
        podeVerComercial() {
            return this.isInRole('prd-orcamentos-consultar')
        },
    },
    watch: {
        unidadeId() {
            this.buscar(1)
        },
        '$route.query.situacao'(valor) {
            const situacao = [1, 2].includes(Number(valor)) ? Number(valor) : 0
            if (this.filtro.situacao !== situacao) {
                this.filtro.situacao = situacao
                this.buscar(1)
            }
        },
    },
    created() {
        this.buscar(1)
    },
    methods: {
        async buscar(pagina) {
            if (!this.unidadeId) return
            this.filtro.pagina = pagina || 1
            this.loading = true
            try {
                const q = new URLSearchParams({ unidadeId: this.unidadeId, pagina: this.filtro.pagina, porPagina: this.filtro.porPagina, situacao: this.filtro.situacao })
                if (this.filtro.procurar) q.append('procurar', this.filtro.procurar)
                if (this.filtro.dataInicial) q.append('dataInicial', this.iso(this.filtro.dataInicial))
                if (this.filtro.dataFinal) q.append('dataFinal', this.iso(this.filtro.dataFinal))
                const r = await http.get(`/api/prd/producao?${q}`)
                this.resultado = r.data
            } catch (e) {
                this.erro(e)
            } finally {
                this.loading = false
            }
        },
        limpar() {
            this.filtro = Object.assign(this.filtro, {
                procurar: '',
                situacao: 0,
                dataInicial: null,
                dataFinal: null,
            })
            this.buscar(1)
        },
        podeCancelar(row) {
            return this.isInRole('prd-pedidos-produzir') && row.status === 9 && !row.dataHoraEntrega
        },
        confirmarCancelamento(row) {
            this.$buefy.dialog.confirm({
                title: 'Cancelar produção',
                message: 'Deseja cancelar esta produção?',
                confirmText: 'Cancelar produção',
                cancelText: 'Voltar',
                type: 'is-danger',
                hasIcon: true,
                onConfirm: () => this.cancelarProducao(row),
            })
        },
        async cancelarProducao(row) {
            this.loading = true
            try {
                await http.post(`/api/prd/producao/${row.id}/cancelar?unidadeId=${this.unidadeId}`)
                this.$buefy.toast.open({ message: 'Produção cancelada. O orçamento voltou com status Recusado.', type: 'is-success' })
                await this.buscar(this.filtro.pagina)
            } catch (e) {
                this.erro(e)
            } finally {
                this.loading = false
            }
        },
        iso(v) {
            return new Date(v).toISOString().slice(0, 10)
        },
        data(v) {
            return v ? new Date(v).toLocaleDateString('pt-BR') : ''
        },
        dataHora(v) {
            return v ? new Date(v).toLocaleString('pt-BR') : '—'
        },
        moeda(v) {
            return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },
        erro(e) {
            this.$buefy.toast.open({ message: e.data?.erro || 'Não foi possível carregar a produção.', type: 'is-danger' })
        },
    },
}
</script>

<style scoped>
.prd-page {
    background: var(--surface-page);
    min-height: calc(100vh - 110px);
    padding: 1rem 1rem !important;
}
.prd-toolbar {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 0.85rem;
}
.prd-toolbar > .field {
    margin-bottom: 0;
}
.prd-search {
    flex: 1;
}
.prd-status-filter {
    width: 180px;
    flex: none;
}
.prd-filter-panel {
    margin: 0 0 0.85rem !important;
    padding: 0.7rem;
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 12px;
}
.prd-table-card {
    background: white;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    overflow: hidden;
}
.prd-origin {
    display: block;
    width: max-content;
    margin-top: 0.2rem;
    padding: 0.08rem 0.3rem;
    border-radius: 5px;
    background: #edf2f7;
    color: #607085;
    font-size: 0.7rem;
}
.prd-subtext {
    display: block;
    margin-top: 0.15rem;
    color: #8492a6;
    font-size: 0.75rem;
}
.prd-value {
    color: var(--brand-navy-800);
}
.prd-row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
}
@media (max-width: 768px) {
    .prd-page {
        padding: 1rem;
    }
    .prd-toolbar {
        flex-wrap: wrap;
    }
    .prd-search {
        flex-basis: 100%;
    }
    .prd-status-filter {
        width: 100%;
    }
}
</style>
